import React, { useEffect, useRef, useState } from "react";
import EmailEditor from "react-email-editor";
import registerAddNewsTool from "./Custom";
import {
    Button,
    Typography,
    useTheme,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    CircularProgress,
    DialogContentText,
    Box,
    Snackbar,
    Alert,
} from "@mui/material";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import { useNavigate, useSearchParams } from "react-router-dom";
import API_ENDPOINTS from "../../apis/endpoints";
import useAuth from "../../context";
import useSWR from "swr";

const Newsletter = () => {
    const emailEditorRef = useRef(null);
    const theme = useTheme();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const auth = useAuth();
    const [newsLibrary, setNewsLibrary] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [templateName, setTemplateName] = useState("");
    const [templateDesign, setTemplateDesign] = useState(null);
    const storedDeviceId = localStorage.getItem("device-id");
    // Add these state variables
    const [openDraftDialog, setOpenDraftDialog] = useState(false);
    const [openContinueDialog, setOpenContinueDialog] = useState(false);
    const [newsletterName, setNewsletterName] = useState("");
    const [openTemplateDialog, setOpenTemplateDialog] = useState(false);
    const [editorLoading, setEditorLoading] = useState(true);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const {
        data,
        isLoading: dataLoading,
        mutate,
    } = useSWR(
        () =>
            searchParams.has("template")
                ? `${API_ENDPOINTS.NEWSLETTER}template/${searchParams.get(
                      "template"
                  )}/`
                : searchParams.has("edit")
                  ? `${API_ENDPOINTS.NEWSLETTER}${searchParams.get(
                        "edit"
                    )}/template/`
                  : null,
        {
            revalidateOnFocus: false,
            revalidateOnMount: true,
            revalidateOnReconnect: false,
        }
    );

    const { data: articleData, isLoading: articleLoading } = useSWR(
        `${API_ENDPOINTS.NEWSLETTER}articles/`,
        {
            revalidateOnFocus: false,
            revalidateOnMount: true,
            revalidateOnReconnect: false,
        }
    );

    // Add these handler functions

    const handleOpenDraftDialog = () => {
        const unlayer = emailEditorRef.current?.editor;
        unlayer?.exportHtml((exported_data) => {
            if (searchParams.get("edit") && data) {
                handleUpdateNewsletter(searchParams.get("edit"), exported_data);
            } else {
                setTemplateDesign(exported_data);
                setOpenDraftDialog(true);
            }
        });
    };

    const handleCloseDraftDialog = () => {
        setOpenDraftDialog(false);
        setNewsletterName("");
    };

    const handleCloseContinueDialog = () => {
        setOpenContinueDialog(false);
        setNewsletterName("");
    };
    const handleCloseTemplateDialog = () => {
        setOpenTemplateDialog(false);
        setTemplateName("");
    };

    const handleSaveDraft = async () => {
        try {
            const formData = new FormData();
            formData.append("design", JSON.stringify(templateDesign.design));
            formData.append("html", templateDesign.html);
            formData.append("name", newsletterName);

            const response = await fetch(`${API_ENDPOINTS.NEWSLETTER}`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${auth.accessToken}`,
                    "x-cc-device-id": storedDeviceId,
                },
                body: formData,
            });

            if (!response.ok) {
                throw new Error("Failed to save draft");
            }

            const result = await response.json();

            const { id } = result.data;

            handleCloseDraftDialog();

            if (!searchParams.has("edit")) {
                navigate(`/dashboard/newsletter/create?edit=${id}`, {
                    replace: true,
                });
            }

            mutate();
        } catch (error) {
            console.error("Error saving draft:", error);
        }
    };
    const handleOpenContinueDialog = () => {
        const unlayer = emailEditorRef.current?.editor;
        unlayer?.exportHtml((exported_data) => {
            if (searchParams.get("edit") && data) {
                handleUpdateNewsletter(
                    searchParams.get("edit"),
                    exported_data,
                    true
                );
            } else {
                handleContinue(exported_data);
            }
        });
    };

    const handleContinue = async (data) => {
        try {
            const formData = new FormData();
            formData.append("design", JSON.stringify(data.design));
            formData.append("html", data.html);
            formData.append("name", newsletterName || "Untitled");

            const response = await fetch(`${API_ENDPOINTS.NEWSLETTER}`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${auth.accessToken}`,
                    "x-cc-device-id": storedDeviceId,
                },
                body: formData,
            });

            if (!response.ok) {
                throw new Error("Failed to save newsletter");
            }

            const result = await response.json();

            mutate();
            navigate(`/dashboard/newsletter/create?edit=${result.data.id}`, {
                replace: true,
            });

            setTimeout(() => {
                navigate(`/dashboard/newsletter/send/${result.data.id}`);
            }, 0);
        } catch (error) {
            console.error("Error saving newsletter:", error);
        }
    };

    const handleUpdateNewsletter = async (id, data, redirect) => {
        try {
            const formData = new FormData();
            formData.append("design", JSON.stringify(data.design));
            formData.append("html", data.html);

            const response = await fetch(
                `${API_ENDPOINTS.NEWSLETTER}${id}/template/`,
                {
                    method: "PATCH",
                    headers: {
                        Authorization: `Bearer ${auth.accessToken}`,
                        "x-cc-device-id": storedDeviceId,
                    },
                    body: formData,
                }
            );

            if (!response.ok) {
                throw new Error("Failed to save newsletter");
            }
            setSnackbar({
                open: true,
                message: "Newsletter saved as draft successfully",
                severity: "success",
            });
            const result = await response.json();

            handleCloseContinueDialog();
            mutate();
            if (redirect) {
                navigate(`/dashboard/newsletter/send/${id}`);
            }
        } catch (error) {
            console.error("Error saving newsletter:", error);
            setSnackbar({
                open: true,
                message: error.message || "Failed to send newsletter",
                severity: "error",
            });
        }
    };

    useEffect(() => {
        if (articleData) {
            setNewsLibrary(articleData);
            editorOptions = {
                customJS: [
                    registerAddNewsTool([
                        ...articleData,
                        API_ENDPOINTS.STORAGE,
                        auth.accessToken,
                        auth.deviceId,
                    ]),
                ],
            };
        }
    }, [articleData]);

    const handleOpenTemplateDialog = () => {
        const unlayer = emailEditorRef?.current?.editor;
        unlayer?.exportHtml((data) => {
            setTemplateDesign(data);
            setOpenTemplateDialog(true);
        });
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setTemplateName("");
    };

    const handleSaveTemplate = async () => {
        try {
            const formData = new FormData();
            formData.append("design", JSON.stringify(templateDesign.design));
            formData.append("name", templateName);

            const response = await fetch(
                `${API_ENDPOINTS.NEWSLETTER}template/`,
                {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${auth.accessToken}`,
                        "x-cc-device-id": storedDeviceId,
                    },
                    body: formData,
                }
            );

            if (!response.ok) {
                throw new Error("Failed to save template");
            }
            setSnackbar({
                open: true,
                message: "Newsletter saved as Template successfully",
                severity: "success",
            });

            handleCloseDialog();
            mutate();
            navigate("/dashboard/newsletter/list");
        } catch (error) {
            console.error("Error saving template:", error);
            setSnackbar({
                open: true,
                message: error.message || "Failed to send newsletter",
                severity: "error",
            });
        }
    };

    const loadTemplate = () => {
        setEditorLoading(false);
        const unlayer = emailEditorRef.current?.editor;
        if (unlayer) {
            if (data) {
                unlayer.loadDesign(data?.design);
            } else {
                unlayer.loadBlank();
            }
            unlayer.registerCallback("image", function (files, done) {
                const file = files.attachments[0];
                let filename = file.name;

                if (!filename.includes(".")) {
                    filename += ".png";
                }

                fetch(`${API_ENDPOINTS.STORAGE}`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${auth.accessToken}`,
                        "X-CC-Device-ID": auth.deviceId,
                    },
                    body: JSON.stringify({
                        public: true,
                        folder_type: "newsletter",
                        folder_id: "common",
                        file_name: filename,
                        file_type: file.type,
                        file_size: file.size,
                    }),
                })
                    .then((response) => {
                        done({ progress: 10 });
                        if (response.status === 200) {
                            return response;
                        } else {
                            var error = new Error(response.statusText);
                            error.response = response;
                            throw error;
                        }
                    })
                    .then((response) => {
                        done({ progress: 20 });
                        return response.json();
                    })
                    .then((data) => {
                        done({ progress: 30 });
                        if (!data || data.error || !data.data) {
                            throw new Error(
                                data?.message || "Invalid response"
                            );
                        }

                        return {
                            upload_url: data.data.upload_url,
                            file_url: data.data.url,
                        };
                    })
                    .then((data) => {
                        done({ progress: 40 });

                        let progress = 40;
                        const interval = setInterval(() => {
                            progress += 1;
                            done({ progress });
                            if (progress >= 80) {
                                clearInterval(interval);
                            }
                        }, 300);

                        fetch(data.upload_url, {
                            method: "PUT",
                            headers: {
                                "Content-Type": file.type,
                            },
                            body: file,
                        }).then((response) => {
                            clearInterval(interval);
                            done({ progress: 90 });
                            if (!response.ok) {
                                var error = new Error(response.statusText);
                                error.response = response;
                                throw error;
                            }

                            done({ progress: 100, url: data.file_url });
                        });
                    });
            });
        }
    };

    const exportHtml = () => {
        const unlayer = emailEditorRef.current?.editor;
        unlayer?.exportHtml((data) => {
            const { design, html } = data;
            const customNewsData = (design.body?.rows || [])
                .flatMap((row) => row.columns || [])
                .flatMap((column) => column.contents || [])
                .filter((content) => content.type === "add_news")
                .map((content) => content.values?.newsItem || "");
            return { design, html, customNewsData };
        });
    };

    var editorOptions = {
        customJS: [registerAddNewsTool(newsLibrary)],
    };

    const initializeEditor = (options) => {
        if (options.customJS) {
            options.customJS.forEach((tool) => {
                if (typeof tool.execute === "function") {
                    tool.execute();
                }
            });
        }
        return options;
    };
    const handleCloseSnackbar = (event, reason) => {
        if (reason === "clickaway") {
            return;
        }
        setSnackbar({ ...snackbar, open: false });
    };

    if (articleLoading || dataLoading) {
        return (
            <Box className="loader-container">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <>
            <div
                style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    marginBottom: "20px",
                    alignItems: "center",
                }}
            >
                <div
                    style={{
                        display: "flex",
                        flexDirection: "row",
                        columnGap: "1rem",
                        alignItems: "center",
                    }}
                >
                    <ArrowBackIosNewRoundedIcon
                        onClick={() => navigate("/dashboard/newsletter")}
                    />
                    <Typography
                        variant="highlightEmphasis"
                        sx={{ color: theme.palette.neutral[200] }}
                    >
                        1/2
                    </Typography>
                    <Typography
                        variant="highlightEmphasis"
                        sx={{ color: theme.palette.neutral[500] }}
                    >
                        Design Newsletter
                    </Typography>
                </div>
                <div
                    style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        columnGap: "1rem",
                        alignItems: "center",
                    }}
                >
                    <Button
                        variant="outlined"
                        onClick={handleOpenTemplateDialog}
                        disabled={
                            dataLoading || articleLoading || editorLoading
                        }
                    >
                        Save as Template
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={handleOpenDraftDialog}
                        disabled={
                            dataLoading || articleLoading || editorLoading
                        }
                    >
                        Save
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleOpenContinueDialog}
                        disabled={
                            dataLoading || articleLoading || editorLoading
                        }
                    >
                        Continue
                    </Button>
                </div>
            </div>
            <div style={{ display: "flex", height: "87vh" }}>
                <EmailEditor
                    projectId={1}
                    ref={emailEditorRef}
                    onReady={loadTemplate}
                    options={initializeEditor(editorOptions)}
                />
            </div>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>

            <Dialog
                open={openTemplateDialog}
                onClose={handleCloseTemplateDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Save as Template</DialogTitle>
                <DialogContentText sx={{ marginLeft: "1.5rem" }}>
                    You can save NewsLetter as template to use it later and use
                    it whenever required
                </DialogContentText>
                <DialogContent sx={{ width: "100%", pt: 2 }}>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Template Name"
                        fullWidth
                        value={templateName}
                        onChange={(e) => setTemplateName(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseTemplateDialog}>Cancel</Button>
                    <Button onClick={handleSaveTemplate} variant="contained">
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Draft Dialog */}
            <Dialog
                open={openDraftDialog}
                onClose={handleCloseDraftDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Save NewsLetter</DialogTitle>
                <DialogContentText sx={{ marginLeft: "1.5rem" }}>
                    You can save NewsLetter as draft and can continue editing
                    whenever required
                </DialogContentText>
                <DialogContent sx={{ width: "100%", pt: 2 }}>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Newsletter Name"
                        fullWidth
                        value={newsletterName}
                        onChange={(e) => setNewsletterName(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDraftDialog}>Cancel</Button>
                    <Button onClick={handleSaveDraft} variant="contained">
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Continue Dialog */}
            <Dialog
                open={openContinueDialog}
                onClose={handleCloseContinueDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Save Newsletter</DialogTitle>
                <DialogContent sx={{ width: "500px", pt: 2 }}>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Newsletter Name"
                        fullWidth
                        value={newsletterName}
                        onChange={(e) => setNewsletterName(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseContinueDialog}>Cancel</Button>
                    <Button onClick={handleContinue} variant="contained">
                        Continue
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default Newsletter;
